import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import {
  LayoutDashboard, CalendarDays, MapPin, Globe, Building2, Trophy,
  Calendar, ClipboardList, Home, Car, Users, User, Link2,
  KeyRound, Award, Map, LogOut, LogIn, UserCog, Database, Pin,
  Leaf, BarChart3
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const SIDEBAR_WIDTH = 280;
const LS_KEY = 'sidebarPinned';

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['navigation', 'common', 'auth']);
  const { user, isAuthenticated, logout, hasRole } = useAuth();
  const { user: partnerUser, logout: partnerLogout } = usePartnerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPartnerUser = partnerUser !== null;

  const [isPinned, setIsPinned] = useState(() => {
    return localStorage.getItem(LS_KEY) === 'true';
  });

  const [collapsedSections, setCollapsedSections] = useState<{[key: string]: boolean}>({
    stammdaten: true,
    einstellungen: true,
    global: true,
    lokal: true,
  });

  useEffect(() => {
    localStorage.setItem(LS_KEY, String(isPinned));
  }, [isPinned]);

  const togglePin = () => setIsPinned(prev => !prev);

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLinkClick = () => {
    if (!isPinned) onClose();
  };

  const handleLogout = () => {
    if (isPartnerUser) {
      partnerLogout();
      navigate('/partner/login');
    } else {
      logout();
      navigate('/');
    }
    onClose();
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  if (!isOpen) return null;

  return (
    <nav style={{ ...sidebarStyle, width: SIDEBAR_WIDTH }} className="app-sidebar">
      {/* Sidebar Header */}
      <div style={sidebarHeaderStyle}>
        <span style={sidebarBrandStyle}>{t('navigation:brand')}</span>
        <button
          onClick={togglePin}
          style={{
            ...pinButtonStyle,
            color: isPinned ? '#3498db' : '#adb5bd',
            transform: isPinned ? 'rotate(0deg)' : 'rotate(45deg)',
          }}
          className="pin-button"
          title={isPinned ? 'Menü lösen' : 'Menü anheften'}
        >
          <Pin size={18} />
        </button>
      </div>

      {/* Menu Content */}
      <div style={menuContentStyle}>
        {/* Dashboard */}
        <Link to={isPartnerUser ? "/partner/dashboard" : "/"} style={{...linkStyle, ...(isActive('/') && !isPartnerUser ? activeLinkStyle : {}), marginBottom: '0.25rem'}} className="sidebar-link" onClick={handleLinkClick}>
          <LayoutDashboard size={16} /> {t('navigation:dashboard')}
        </Link>

        {/* Upcoming Matches */}
        {isPartnerUser ? (
          <Link to="/partner/matchday-planning" style={{...linkStyle, ...(isActive('/partner/matchday-planning') ? activeLinkStyle : {}), marginBottom: '0.5rem'}} className="sidebar-link" onClick={handleLinkClick}>
            <CalendarDays size={16} /> {t('navigation:upcomingMatches')}
          </Link>
        ) : isAuthenticated ? (
          <Link to="/upcoming-matches" style={{...linkStyle, ...(isActive('/upcoming-matches') ? activeLinkStyle : {}), marginBottom: '0.5rem'}} className="sidebar-link" onClick={handleLinkClick}>
            <CalendarDays size={16} /> {t('navigation:upcomingMatches')}
          </Link>
        ) : (
          <Link to="/login" style={{...linkStyle, marginBottom: '0.5rem'}} className="sidebar-link" onClick={handleLinkClick}>
            <CalendarDays size={16} /> {t('navigation:upcomingMatches')}
          </Link>
        )}

        {/* Tracking */}
        {isAuthenticated && !isPartnerUser && (
          <Link to="/tracking" style={{...linkStyle, ...(isActive('/tracking') ? activeLinkStyle : {}), marginBottom: '1rem'}} className="sidebar-link" onClick={handleLinkClick}>
            <MapPin size={16} /> {t('navigation:game.tracking')}
          </Link>
        )}

        {/* === Authenticated regular user sections === */}
        {isAuthenticated && !isPartnerUser && (
          <>
            {/* STAMMDATEN */}
            <div style={sectionStyle}>
              <h4 style={{...sectionTitleStyle, cursor: 'pointer'}} onClick={() => toggleSection('stammdaten')}>
                {collapsedSections.stammdaten ? '▶' : '▼'} {t('navigation:sections.masterData')}
              </h4>
              {!collapsedSections.stammdaten && (
                <>
                  {/* Global */}
                  <div style={submenuStyle}>
                    <h5 style={{...submenuTitleStyle, cursor: 'pointer'}} onClick={() => toggleSection('global')}>
                      {collapsedSections.global ? '▶' : '▼'} <Globe size={14} /> {t('navigation:masterData.global.title')}
                    </h5>
                    {!collapsedSections.global && (
                      <>
                        <Link to="/countries" style={{...subLinkStyle, ...(isActive('/countries') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Globe size={14} /> {t('navigation:masterData.global.countries')}</Link>
                        <Link to="/leagues" style={{...subLinkStyle, ...(isActive('/leagues') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Trophy size={14} /> {t('navigation:masterData.global.leagues')}</Link>
                        <Link to="/clubs" style={{...subLinkStyle, ...(isActive('/clubs') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Building2 size={14} /> {t('navigation:masterData.global.clubs')}</Link>
                        <Link to="/seasons" style={{...subLinkStyle, ...(isActive('/seasons') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Calendar size={14} /> {t('navigation:masterData.global.seasons')}</Link>
                        <Link to="/competitions" style={{...subLinkStyle, ...(isActive('/competitions') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Award size={14} /> {t('navigation:masterData.global.competitions')}</Link>
                        <Link to="/matchdays" style={{...subLinkStyle, ...(isActive('/matchdays') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><ClipboardList size={14} /> {t('navigation:masterData.global.matchdays')}</Link>
                        <Link to="/matches" style={{...subLinkStyle, ...(isActive('/matches') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><CalendarDays size={14} /> {t('navigation:masterData.global.matches')}</Link>
                        <Link to="/partners" style={{...subLinkStyle, ...(isActive('/partners') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Link2 size={14} /> {t('navigation:masterData.global.partners')}</Link>
                      </>
                    )}
                  </div>
                  {/* Lokal */}
                  <div style={submenuStyle}>
                    <h5 style={{...submenuTitleStyle, cursor: 'pointer'}} onClick={() => toggleSection('lokal')}>
                      {collapsedSections.lokal ? '▶' : '▼'} <Home size={14} /> {t('navigation:masterData.local.title')}
                    </h5>
                    {!collapsedSections.lokal && (
                      <>
                        <Link to="/local-matches" style={{...subLinkStyle, ...(isActive('/local-matches') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><CalendarDays size={14} /> {t('navigation:masterData.local.matches')}</Link>
                        <Link to="/local-matchdays" style={{...subLinkStyle, ...(isActive('/local-matchdays') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><ClipboardList size={14} /> {t('navigation:masterData.local.matchdays')}</Link>
                        <Link to="/transportation" style={{...subLinkStyle, ...(isActive('/transportation') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Car size={14} /> {t('navigation:masterData.local.transportation')}</Link>
                        <Link to="/travelers" style={{...subLinkStyle, ...(isActive('/travelers') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Users size={14} /> {t('navigation:masterData.local.travelers')}</Link>
                        <Link to="/travelers?type=Driver" style={{...subLinkStyle, ...(location.search.includes('type=Driver') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><User size={14} /> {t('navigation:masterData.local.drivers')}</Link>
                        <Link to="/travelers?type=Player" style={{...subLinkStyle, ...(location.search.includes('type=Player') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><UserCog size={14} /> {t('navigation:masterData.local.players')}</Link>
                        <Link to="/custom-partners" style={{...subLinkStyle, ...(isActive('/custom-partners') ? activeSubLinkStyle : {})}} className="sidebar-sublink" onClick={handleLinkClick}><Link2 size={14} /> {t('navigation:masterData.local.partners')}</Link>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* EINSTELLUNGEN */}
            <div style={sectionStyle}>
              <h4 style={{...sectionTitleStyle, cursor: 'pointer'}} onClick={() => toggleSection('einstellungen')}>
                {collapsedSections.einstellungen ? '▶' : '▼'} {t('navigation:sections.settings')}
              </h4>
              {!collapsedSections.einstellungen && (
                <>
                  <Link to="/user-management" style={{...linkStyle, ...(isActive('/user-management') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><Users size={14} /> {t('navigation:settings.userManagement')}</Link>
                  <Link to="/data-management" style={{...linkStyle, ...(isActive('/data-management') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><Database size={14} /> {t('navigation:settings.dataManagement')}</Link>
                  <Link to="/sports-management" style={{...linkStyle, ...(isActive('/sports-management') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><Trophy size={14} /> {t('navigation:settings.sportsManagement')}</Link>
                  <Link to="/partner-access" style={{...linkStyle, ...(isActive('/partner-access') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><KeyRound size={14} /> {t('navigation:settings.partnerAccess')}</Link>
                  <Link to="/settings/tomtom" style={{...linkStyle, ...(isActive('/settings/tomtom') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><Map size={14} /> {t('navigation:settings.tomtom')}</Link>
                  {hasRole('Admin') && (
                    <>
                      <Link to="/settings/emission-factors" style={{...linkStyle, ...(isActive('/settings/emission-factors') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><Leaf size={14} /> {t('navigation:settings.emissionFactors')}</Link>
                      <Link to="/sustainability" style={{...linkStyle, ...(isActive('/sustainability') ? activeLinkStyle : {})}} className="sidebar-link" onClick={handleLinkClick}><BarChart3 size={14} /> {t('navigation:settings.sustainability')}</Link>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Logout */}
            <div style={sectionStyle}>
              <button onClick={handleLogout} style={logoutButtonStyle} className="sidebar-logout-button">
                <LogOut size={14} /> {t('auth:logout.title')}
              </button>
            </div>
          </>
        )}

        {/* Partner Logout */}
        {isPartnerUser && (
          <div style={sectionStyle}>
            <button onClick={handleLogout} style={logoutButtonStyle} className="sidebar-logout-button">
              <LogOut size={14} /> {t('auth:logout.title')}
            </button>
          </div>
        )}

        {/* Not authenticated */}
        {!isAuthenticated && !isPartnerUser && (
          <Link to="/login" style={linkStyle} className="sidebar-link" onClick={handleLinkClick}>
            <LogIn size={14} /> {t('auth:login.title')}
          </Link>
        )}
      </div>
    </nav>
  );
};

export { SIDEBAR_WIDTH, LS_KEY };
export default Sidebar;

// --- Styles ---

const sidebarStyle: React.CSSProperties = {
  position: 'relative',
  height: 'calc(100vh - 49px)',
  backgroundColor: '#ffffff',
  borderRight: '1px solid #e9ecef',
  overflowY: 'auto',
  flexShrink: 0,
};

const sidebarHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.25rem',
  borderBottom: '1px solid #e9ecef',
  backgroundColor: '#f8f9fa',
};

const sidebarBrandStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#2c3e50',
};

const pinButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'color 0.2s, transform 0.3s',
};

const menuContentStyle: React.CSSProperties = {
  padding: '0.75rem 0',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '0.5rem',
  paddingBottom: '0.5rem',
  borderBottom: '1px solid #f1f3f4',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 0.5rem 0',
  padding: '0.4rem 1.25rem',
  fontSize: '0.8rem',
  fontWeight: '600',
  color: '#6c757d',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const linkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#2c3e50',
  textDecoration: 'none',
  padding: '0.5rem 1.25rem',
  fontSize: '0.9rem',
  fontWeight: '500',
  transition: 'background-color 0.15s, color 0.15s',
  borderLeft: '3px solid transparent',
};

const activeLinkStyle: React.CSSProperties = {
  backgroundColor: '#eef2ff',
  borderLeftColor: '#3498db',
  color: '#2980b9',
};

const submenuStyle: React.CSSProperties = {
  marginBottom: '0.5rem',
  paddingLeft: '0.75rem',
  borderLeft: '2px solid #e9ecef',
  marginLeft: '1.25rem',
};

const submenuTitleStyle: React.CSSProperties = {
  margin: '0 0 0.25rem 0',
  padding: '0.35rem 0',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#495057',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const subLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: '#495057',
  textDecoration: 'none',
  padding: '0.35rem 0.75rem',
  fontSize: '0.85rem',
  fontWeight: '400',
  transition: 'background-color 0.15s, color 0.15s',
  borderLeft: '3px solid transparent',
  marginLeft: '-0.75rem',
};

const activeSubLinkStyle: React.CSSProperties = {
  backgroundColor: '#e8f5e9',
  borderLeftColor: '#28a745',
  color: '#1e7e34',
};

const logoutButtonStyle: React.CSSProperties = {
  width: 'calc(100% - 2.5rem)',
  margin: '0 1.25rem',
  background: '#e74c3c',
  border: 'none',
  color: 'white',
  padding: '0.6rem 0.75rem',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  transition: 'background-color 0.2s',
  textAlign: 'left' as const,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

// Dynamic hover styles
const sidebarCss = document.createElement('style');
sidebarCss.textContent = `
  .sidebar-link:hover {
    background-color: #f8f9fa !important;
    border-left-color: #3498db !important;
    color: #2980b9 !important;
  }
  .sidebar-sublink:hover {
    background-color: #f1f3f4 !important;
    border-left-color: #28a745 !important;
    color: #1e7e34 !important;
  }
  .sidebar-logout-button:hover {
    background-color: #c0392b !important;
  }
  .pin-button:hover {
    background-color: #e9ecef !important;
  }
  .app-sidebar {
    scrollbar-width: thin;
    scrollbar-color: #dee2e6 transparent;
  }
  .app-sidebar::-webkit-scrollbar {
    width: 6px;
  }
  .app-sidebar::-webkit-scrollbar-thumb {
    background-color: #dee2e6;
    border-radius: 3px;
  }
`;
if (!document.querySelector('[data-sidebar-styles]')) {
  sidebarCss.setAttribute('data-sidebar-styles', '');
  document.head.appendChild(sidebarCss);
}

